import "../style/style.less";

import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { ScopedCssBaseline, ThemeProvider as MuiThemeProvider } from "@mui/material";
import { ThemeProvider as ScThemeProvider } from "styled-components";
import * as Sentry from "@sentry/react";
import { SnackbarProvider } from "notistack";
import React, { Suspense, useEffect } from "react";
import { Helmet } from "react-helmet";
import ReactModal from "react-modal";
import { Outlet, Route, Routes } from "react-router-dom";
import useRoutingInstrumentation from "react-router-v6-instrumentation";
import { StoreProvider } from "easy-peasy";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import withScrolling from "react-dnd-scrolling";

import StyleGuide from "../store/state/inheritFromWeeb-ui-v2/areas/diag/components/StyleGuide";
import theme from "../theme/theme";
import dolbyCallStore from "../store/state/dolby-call";
import { lazyWithRetry as lazy } from "../lib/react";
import { InitSentry } from "../setupSentry";
import { RequireAuth } from "../Pages/auth/require_auth"; // Require Authentication High Order Component
import Metadata from "../Pages/story_authoring/Metadata/Metadata";
import Notes from "../Pages/story_authoring/Index/Notes";

import { NewLayout } from "./nav/NewLayout";
import { GroupContextProvider } from "./common/GroupContext.js";

const MagicLink = lazy(
  () =>
    import(
      "../Pages/auth/magicLink"
      /* webpackChunkName: "components/magicLink" */
      /* webpackPrefetch: true */
    )
); // Magic Link auth page
const JoinProjectMagicLink = lazy(
  () =>
    import(
      "../Pages/auth/joinProjectMagicLink"
      /* webpackChunkName: "components/magicLink" */
      /* webpackPrefetch: true */
    )
); // Magic Link auth page
const JoinOrganizationMagicLink = lazy(
  () =>
    import(
      "../Pages/auth/joinOrganizationMagicLink"
      /* webpackChunkName: "components/magicLink" */
      /* webpackPrefetch: true */
    )
); // Magic Link auth page
const ErrorPage = lazy(
  () =>
    import(
      "../Pages/ErrorPages/ErrorPage"
      /* webpackChunkName: "components/magicLink" */
      /* webpackPrefetch: true */
    )
); // Magic Link auth page
const PasswordRecovery = lazy(
  () =>
    import(
      "../Pages/auth/passwordRecovery"
      /* webpackChunkName: "components/passwordRecovery" */
      /* webpackPrefetch: true */
    )
); // password recover Page
const PasswordUpdate = lazy(
  () =>
    import(
      "../Pages/auth/passwordUpdate"
      /* webpackChunkName: "components/passwordUpdate" */
      /* webpackPrefetch: true */
    )
); // password update/renewal Page
const SSOIdentityProviderDiscovery = lazy(
  () =>
    import(
      "../Pages/auth/ssoIdentityProviderDiscovery"
      /* webpackChunkName: "components/ssoIdentityProviderDiscovery" */
      /* webpackPrefetch: true */
    )
);
const Signout = lazy(
  () =>
    import(
      "../Pages/auth/signout"
      /* webpackChunkName: "components/signout" */
      /* webpackPrefetch: true */
    )
); // Sign out Page
const Ticket = lazy(
  () =>
    import(
      "../Pages/auth/ticket"
      /* webpackChunkName: "components/ticket" */
      /* webpackPrefetch: true */
    )
); // Sign out Page
const Signup = lazy(
  () =>
    import(
      "../Pages/auth/signup"
      /* webpackChunkName: "components/signup" */
      /* webpackPrefetch: true */
    )
); // Sign up Page
const AboutUs = lazy(
  () =>
    import(
      "../Pages/About/AboutUs"
      /* webpackChunkName: "components/aboutUs" */
      /* webpackPrefetch: true */
    )
);
const TicketUsed = lazy(
  () =>
    import(
      "../Pages/admin/ticketUsed"
      /* webpackChunkName: "components/aboutUs" */
      /* webpackPrefetch: true */
    )
);
const Admin = lazy(
  () =>
    import(
      "../Pages/admin/admin"
      /* webpackChunkName: "components/admin" */
      /* webpackPrefetch: true */
    )
);
const OrganizationDetails = lazy(
  () =>
    import(
      "../Pages/admin/organizationDetails"
      /* webpackChunkName: "components/organizationDetails" */
      /* webpackPrefetch: true */
    )
);

const NewProjectConclude = lazy(
  () =>
    import(
      "../Pages/ConcludeV2/ConcludePage"
      /* webpackChunkName: "components/conclude" */
      /* webpackPrefetch: true */
    )
);

const Features = lazy(
  () =>
    import(
      "../Pages/features/Features"
      /* webpackChunkName: "components/features" */
      /* webpackPrefetch: true */
    )
); //  Features page
const Hub = lazy(
  () =>
    import(
      "../Pages/Hub/HubPage"
      /* webpackChunkName: "components/HubPage" */
      /* webpackPrefetch: true */
    )
); //  Features page

const StaticSiteFooter = lazy(
  () =>
    import(
      "./StaticSiteFooter"
      /* webpackChunkName: "components/footer" */
      /* webpackPrefetch: true */
    )
);
const FAQ = lazy(
  () =>
    import(
      "../Pages/GettingStarted/FAQ"
      /* webpackChunkName: "components/faq" */
      /* webpackPrefetch: true */
    )
);
const GettingStarted = lazy(
  () =>
    import(
      "../Pages/GettingStarted/GettingStarted"
      /* webpackChunkName: "components/gettingStarted" */
      /* webpackPrefetch: true */
    )
);
const GroupAcceptInvite = lazy(
  () =>
    import(
      "../Pages/groups/groupAcceptInvite"
      /* webpackChunkName: "components/groupAcceptInvite" */
      /* webpackPrefetch: true */
    )
);
const GroupCreate = lazy(
  () =>
    import(
      "../Pages/groups/groupCreate"
      /* webpackChunkName: "components/groupCreate" */
      /* webpackPrefetch: true */
    )
);
const GroupInvite = lazy(
  () =>
    import(
      "../Pages/groups/groupInvite"
      /* webpackChunkName: "components/groupInvite" */
      /* webpackPrefetch: true */
    )
);
const GroupShow = lazy(
  () =>
    import(
      "../Pages/groups/groupShow"
      /* webpackChunkName: "components/groupShow" */
      /* webpackPrefetch: true */
    )
);
const StaticSiteHeader = lazy(
  () =>
    import(
      "./StaticSiteHeader"
      /* webpackChunkName: "components/header" */
      /* webpackPrefetch: true */
    )
); // StaticSiteHeader Page
const LinkExpired = lazy(
  () =>
    import(
      "./link_expired"
      /* webpackChunkName: "components/linkExpired" */
      /* webpackPrefetch: true */
    )
);
const ModalsContainer = lazy(
  () =>
    import(
      "./modals/ModalsContainer"
      /* webpackChunkName: "components/modalsContainer" */
      /* webpackPrefetch: true */
    )
);
const AppFooter = lazy(
  () =>
    import(
      "./AppFooter"
      /* webpackChunkName: "components/newFooter" */
      /* webpackPrefetch: true */
    )
);
const AppHeader = lazy(
  () =>
    import(
      "./AppHeader"
      /* webpackChunkName: "components/newHeader" */
      /* webpackPrefetch: true */
    )
);
const ForCommunities = lazy(
  () =>
    import(
      "./PopularProjects/ForCommunities"
      /* webpackChunkName: "components/forCommunities" */
      /* webpackPrefetch: true */
    )
); // For Communities page
const ForIndividuals = lazy(
  () =>
    import(
      "./PopularProjects/ForIndividuals"
      /* webpackChunkName: "components/forIndividuals" */
      /* webpackPrefetch: true */
    )
); // For Individuals page
const Prepare = lazy(
  () =>
    import(
      "../Pages/prepare/prepare"
      /* webpackChunkName: "components/prepare" */
      /* webpackPrefetch: true */
    )
);
const PrivacyPolicy = lazy(
  () =>
    import(
      "../Pages/privacyPolicy/privacy_policy"
      /* webpackChunkName: "components/privacyPolicy" */
      /* webpackPrefetch: true */
    )
);
const StoryAuthoring = lazy(
  () =>
    import(
      "../Pages/story_authoring"
      /* webpackChunkName: "components/storyAuthoring" */
      /* webpackPrefetch: true */
    )
);
const FirstCaseStudy = lazy(
  () =>
    import(
      "../Pages/Study/FirstCaseStudy"
      /* webpackChunkName: "components/firstCaseStudy" */
      /* webpackPrefetch: true */
    )
); //  FirstCaseStudy page
const SecondCaseStudy = lazy(
  () =>
    import(
      "../Pages/Study/SecondCaseStudy"
      /* webpackChunkName: "components/secondCaseStudy" */
      /* webpackPrefetch: true */
    )
); //  SecondCaseStudy page
const ThirdCaseStudy = lazy(
  () =>
    import(
      "../Pages/Study/ThirdCaseStudy"
      /* webpackChunkName: "components/thirdCaseStudy" */
      /* webpackPrefetch: true */
    )
); //  ThirdCaseStudy page
const TermsAndConditions = lazy(
  () =>
    import(
      "../Pages/termsAndConditions/terms_and_conditions"
      /* webpackChunkName: "components/termsAndConditions" */
      /* webpackPrefetch: true */
    )
);
const ThankYou = lazy(
  () =>
    import(
      "../Pages/thankYou/thank_you"
      /* webpackChunkName: "components/thankYou" */
      /* webpackPrefetch: true */
    )
);

const UploadFiles = lazy(
  () =>
    import(
      "../Pages/UploadFiles/UploadFiles"
      /* webpackChunkName: "components/uploadSession" */
      /* webpackPrefetch: true */
    )
);

const SelectCloudFiles = lazy(
  () =>
    import(
      "../Pages/UploadFiles/SelectCloudFiles"
      /* webpackChunkName: "components/uploadSession" */
      /* webpackPrefetch: true */
    )
);

const Notifier = lazy(
  () =>
    import(
      "./utils/notifier"
      /* webpackChunkName: "components/notifier" */
      /* webpackPrefetch: true */
    )
);
const LocalRecordingStatus = lazy(
  () =>
    import(
      "./utils/status/all_local_recording_uploads"
      /* webpackChunkName: "components/localRecordingStatus" */
      /* webpackPrefetch: true */
    )
);
/* const Tutorial = lazy(
  () =>
    import(
      "./tutorial"
    )
); */
const Tutorial2 = lazy(
  () =>
    import(
      "../Pages/tutorials/Tutorials"
      /* webpackChunkName: "components/tutorial" */
      /* webpackPrefetch: true */
    )
);
const VideoSession = lazy(
  () =>
    import(
      "./video_session"
      /* webpackChunkName: "components/videoSession" */
      /* webpackPrefetch: true */
    )
); // VideoSession Page
const Welcome = lazy(
  () =>
    import(
      "../Pages/welcome/welcome"
      /* webpackChunkName: "components/welcome" */
      /* webpackPrefetch: true */
    )
); // Welcome Page, interested in putting at root route
const Home = lazy(
  () =>
    import(
      "../Pages/home/home"
      /* webpackChunkName: "components/home" */
      /* webpackPrefetch: true */
    )
);

const MyInterviews = lazy(
  () =>
    import(
      "../Pages/MyInterviews/MyInterviews"
      /* webpackChunkName: "components/home" */
      /* webpackPrefetch: true */
    )
);

const Clips = lazy(
  () =>
    import(
      "../Pages/Clips/ClipsPage"
      /* webpackChunkName: "components/home" */
      /* webpackPrefetch: true */
    )
);

const Login = lazy(
  () =>
    import(
      "../Pages/auth/login"
      /* webpackChunkName: "components/login" */
      /* webpackPrefetch: true */
    )
);
const OrganizationPage = lazy(
  () =>
    import(
      "../Pages/Organization/OrganizationPage"
      /* webpackChunkName: "components/organizationDetails" */
      /* webpackPrefetch: true */
    )
);
const OrganizationsPage = lazy(
  () =>
    import(
      "../Pages/Organization/OrganizationsPage"
      /* webpackChunkName: "components/organizationDetails" */
      /* webpackPrefetch: true */
    )
);
const ProjectPage = lazy(
  () =>
    import(
      "../Pages/Project/ProjectPage"
      /* webpackChunkName: "components/home" */
      /* webpackPrefetch: true */
    )
);
const FolderPage = lazy(
  () =>
    import(
      "../Pages/Folder/FolderPage"
      /* webpackChunkName: "components/home" */
      /* webpackPrefetch: true */
    )
);

const OrganizationProjectFolderPage = lazy(
  () =>
    import(
      "../Pages/Folder/FolderPage"
      /* webpackChunkName: "components/home" */
      /* webpackPrefetch: true */
    )
);

const OrganizationProjectsPage = lazy(
  () =>
    import(
      "../Pages/Projects/ProjectsPage"
      /* webpackChunkName: "components/home" */
      /* webpackPrefetch: true */
    )
);

const OrganizationProjectPage = lazy(
  () =>
    import(
      "../Pages/Project/ProjectPage"
      /* webpackChunkName: "components/home" */
      /* webpackPrefetch: true */
    )
);

const ProjectsPage = lazy(
  () =>
    import(
      "../Pages/Projects/ProjectsPage"
      /* webpackChunkName: "components/home" */
      /* webpackPrefetch: true */
    )
);

const PrepSessionRedirection = lazy(
  () =>
    import(
      "../Pages/prepSessionRedirection/prepRedirection"
      /* webpackChunkName: "components/home" */
      /* webpackPrefetch: true */
    )
);

const EditMetadataFiles = lazy(() => import("../Pages/UploadFiles/editMetadataFiles"));

const InterviewDolby = lazy(() => import("./Interview_dolby/index"));

// https://github.com/reactjs/react-modal/issues/576#issuecomment-383405608
if (typeof window !== "undefined") {
  ReactModal.setAppElement("body");
}

// TODO(js): Use https://www.npmjs.com/package/react-router-v6-instrumentation
// const SentryRoute = Sentry.withSentryRouting(Route);
const SentryRoute = Route;

const staticSitePaths = [
  "/welcome",
  "/individuals",
  "/communities",
  "/features",
  "/case-study",
  "/terms-and-conditions",
  "/privacy-policy",
  "/about-us",
  "/getting-started",
  "/signout",
  "/signup",
  "/login",
  "/FAQ",
];

const callSessionPaths = [
  "/stories/:story_id/start",
  "/stories/:story_id",
  "/call",
  "/:group_name/:custom_url_title/s/:key",
  "/:group_name/:custom_url_title/s/:key/:prepSession_type",
];

const ScrollingComponent = withScrolling("div");

const ConditionalSection = (StaticSite: React.ComponentType<any>, InApp: React.ComponentType<any>) => (
  <Routes>
    <SentryRoute
      path="/welcome"
      element={
        <div className="home-page">
          <StaticSite />
        </div>
      }
    />
    {staticSitePaths.map((path) => (
      <SentryRoute key={path} path={path} element={<StaticSite />} />
    ))}
    {callSessionPaths.map((path) => (
      <SentryRoute key={path} path={path} />
    ))}
    <SentryRoute path="*" element={<InApp />} />
  </Routes>
);

const ConditionalHeader = () => ConditionalSection(StaticSiteHeader, AppHeader);
const ConditionalFooter = () => (
  <Routes>
    <SentryRoute path="/stories/:story_id/author" element={null} />
    <SentryRoute path="/stories/:story_id/author/*" element={null} />
    <SentryRoute path="*" element={ConditionalSection(StaticSiteFooter, AppFooter)} />
  </Routes>
);

function withTitle(pageTitle: string, children: React.ReactNode) {
  return (
    <>
      <Helmet>
        <title>{pageTitle === "" ? "TheirStory" : `${pageTitle} - TheirStory`}</title>
      </Helmet>
      {children}
    </>
  );
}

function AppRoutes() {
  const TutorialNodeType = Tutorial2;

  return (
    <Routes>
      {/* Marketing Site Routes */}
      <SentryRoute path="/welcome" element={<Welcome />} />
      <SentryRoute path="/terms-and-conditions" element={<TermsAndConditions />} />
      <SentryRoute path="/privacy-policy" element={<PrivacyPolicy />} />
      <SentryRoute path="/about-us" element={<AboutUs />} />
      <SentryRoute path="/getting-started" element={<GettingStarted />} />
      <SentryRoute path="/FAQ" element={<FAQ />} />
      {/* Temporarily disabling the pricing page until we figure out a 
                      more permanent price strategy -- DXM 2021-03-04
                   <SentryRoute path="/pricing" element={<Pricing/>} /> */}
      <SentryRoute path="/individuals" element={<ForIndividuals />} />
      <SentryRoute path="/communities" element={<ForCommunities />} />
      <SentryRoute path="/features" element={<Features />} />
      <SentryRoute path="/case-study/1" element={<FirstCaseStudy />} />
      <SentryRoute path="/case-study/2" element={<SecondCaseStudy />} />
      <SentryRoute path="/case-study/3" element={<ThirdCaseStudy />} />

      {/* ------- END OF MARKETING SITE ROUTES ------ */}
      <Route
        element={
          <ScopedCssBaseline style={{ height: "100%" }}>
            <NewLayout>
              <Outlet />
            </NewLayout>
          </ScopedCssBaseline>
        }
      >
        <SentryRoute
          index
          element={withTitle(
            "",
            <RequireAuth>
              <Home />
            </RequireAuth>
          )}
        />
        {/* TODO: We shouldn't have to list VideoSession 4 different types but rather
        use a much simpler `callSessionPaths` list. Unfortunately, there's some
        weird logic in VideoSession where if it doesn't get unmounted/remouted few
        times, it won't actually start a call session. At some point we should fix
        that and then come back and clean this place up
        -- DXM 2021-10-16
      */}
        <SentryRoute path="/stories/:story_id/start" element={withTitle("Video Session", <VideoSession key={1} />)} />

        {/*        <SentryRoute path="/stories/:story_id" element={withTitle("Video Session", <VideoSession key={2} />)} />
         */}
        <SentryRoute
          path="/stories/:interviewId"
          element={withTitle(
            "Video Session",
            <StoreProvider store={dolbyCallStore}>
              <InterviewDolby />
            </StoreProvider>
          )}
        />
        <SentryRoute
          path="/projects/:project_id/stories/:story_id/call"
          element={withTitle("Video Session", <VideoSession key={3} />)}
        />
        <SentryRoute path="/projects/:project_id/call" element={withTitle("Video Session", <VideoSession key={4} />)} />
        <SentryRoute path="/call" element={withTitle("Video Session", <VideoSession key={5} />)} />
        <SentryRoute
          path="/:group_name/project/:project_id/:custom_url_title/s/:key"
          element={withTitle("Video Session", <VideoSession key={7} />)}
        />
        <SentryRoute
          path="/:group_name/:custom_url_title/s/:key"
          element={withTitle("Video Session", <VideoSession key={6} />)}
        />
        <SentryRoute
          path="/:group_name/:custom_url_title/s/:key/:prepSession_type"
          element={withTitle("Video Session", <PrepSessionRedirection key={6} />)}
        />
        <SentryRoute
          path="organization/:organization_id/projects/:project_id/folders/:folder_id/stories/:story_id/hub"
          element={withTitle(
            "Hub page",
            <RequireAuth>
              <Hub />
            </RequireAuth>
          )}
        />
        <SentryRoute
          path="organization/:organization_id/projects/:project_id/stories/:story_id/hub"
          element={withTitle(
            "Hub page",
            <RequireAuth>
              <Hub />
            </RequireAuth>
          )}
        />
        <SentryRoute
          path="projects/:project_id/folders/:folder_id/stories/:story_id/hub"
          element={withTitle(
            "Hub page",
            <RequireAuth>
              <Hub />
            </RequireAuth>
          )}
        />
        <SentryRoute
          path="projects/:project_id/stories/:story_id/hub"
          element={withTitle(
            "Hub page",
            <RequireAuth>
              <Hub />
            </RequireAuth>
          )}
        />
        <SentryRoute
          path="myInterviews/stories/:story_id/hub"
          element={withTitle(
            "Hub page",
            <RequireAuth>
              <Hub />
            </RequireAuth>
          )}
        />
        <SentryRoute
          path="/session"
          element={withTitle(
            "Video Session",
            <StoreProvider store={dolbyCallStore}>
              <InterviewDolby />
            </StoreProvider>
          )}
        />
        <SentryRoute
          path="/projects/:project_id/session"
          element={withTitle(
            "Video Session",
            <StoreProvider store={dolbyCallStore}>
              <InterviewDolby />
            </StoreProvider>
          )}
        />
        <SentryRoute
          path="/projects/:project_id/folders/:folder_id/session"
          element={withTitle(
            "Video Session",
            <StoreProvider store={dolbyCallStore}>
              <InterviewDolby />
            </StoreProvider>
          )}
        />
        <SentryRoute
          path="/session/:interviewId"
          element={withTitle(
            "Video Session",
            <StoreProvider store={dolbyCallStore}>
              <InterviewDolby />
            </StoreProvider>
          )}
        />
        <SentryRoute
          path="/projects/:project_id/session/:interviewId"
          element={withTitle(
            "Video Session",
            <StoreProvider store={dolbyCallStore}>
              <InterviewDolby />
            </StoreProvider>
          )}
        />
        <SentryRoute
          path="/projects/:project_id/folders/:folder_id/session/:interviewId"
          element={withTitle(
            "Video Session",
            <StoreProvider store={dolbyCallStore}>
              <InterviewDolby />
            </StoreProvider>
          )}
        />
        <SentryRoute
          path="/interview_dolby"
          element={withTitle(
            "Video Session",
            <StoreProvider store={dolbyCallStore}>
              <InterviewDolby />
            </StoreProvider>
          )}
        />
        <SentryRoute
          path="/projects/:project_id/interview_dolby"
          element={withTitle(
            "Video Session",
            <StoreProvider store={dolbyCallStore}>
              <InterviewDolby />
            </StoreProvider>
          )}
        />
        <SentryRoute
          path="/interview_dolby/:interviewId"
          element={withTitle(
            "Video Session",
            <StoreProvider store={dolbyCallStore}>
              <InterviewDolby />
            </StoreProvider>
          )}
        />
        <SentryRoute
          path="/projects/:project_id/interview_dolby/:interviewId"
          element={withTitle(
            "Video Session",
            <StoreProvider store={dolbyCallStore}>
              <InterviewDolby />
            </StoreProvider>
          )}
        />
        <SentryRoute
          path="/prepare/projects/:project_id"
          element={withTitle(
            "Prepare a Session",
            <RequireAuth>
              <Prepare />
            </RequireAuth>
          )}
        />
        <SentryRoute
          path="/prepare/organizations/:organization_code/projects/:project_id"
          element={withTitle(
            "Prepare a Session",
            <RequireAuth>
              <Prepare />
            </RequireAuth>
          )}
        />
        <SentryRoute
          path="/myInterviews/"
          element={withTitle(
            "My Interviews",
            <RequireAuth>
              <MyInterviews />
            </RequireAuth>
          )}
        />
        <SentryRoute
          path="/prepare/"
          element={withTitle(
            "Prepare a Session",
            <RequireAuth>
              <Prepare />
            </RequireAuth>
          )}
        />
        <SentryRoute
          path="/projects/:project_id/upload_files"
          element={withTitle(
            "File Upload",
            <RequireAuth>
              <UploadFiles />
            </RequireAuth>
          )}
        />
        <SentryRoute
          path="/projects/:project_id/folders/:folder_id/upload_files"
          element={withTitle(
            "File Upload",
            <RequireAuth>
              <UploadFiles />
            </RequireAuth>
          )}
        />
        <SentryRoute
          path="/projects/select_cloud_files"
          element={withTitle(
            "Select Cloud Files",
            <RequireAuth>
              <SelectCloudFiles />
            </RequireAuth>
          )}
        />
        <SentryRoute
          path="/prepare/:prepsession_id"
          element={withTitle(
            "Prepare a Session",
            <RequireAuth>
              <Prepare />
            </RequireAuth>
          )}
        />
        <SentryRoute path="/magic-link" element={withTitle("Authenticating", <MagicLink />)} />
        <SentryRoute path="/project/:code" element={withTitle("Join Project Magic Link", <JoinProjectMagicLink />)} />
        <SentryRoute
          path="/organization/:code/accept"
          element={withTitle("Join Organization Magic Link", <JoinOrganizationMagicLink />)}
        />
        <SentryRoute path="/login" element={withTitle("Login", <Login />)} />
        <SentryRoute path="/signup" element={withTitle("Sign Up", <Signup />)} />
        <SentryRoute path="/sso" element={withTitle("Sign On", <SSOIdentityProviderDiscovery />)} />
        <SentryRoute path="/password_recovery" element={withTitle("Password Recovery", <PasswordRecovery />)} />
        <SentryRoute path="/password/new/:token" element={withTitle("Update Password", <PasswordUpdate />)} />
        <SentryRoute path="/signout" element={withTitle("Sign Out", <Signout />)} />
        <SentryRoute path="/ticket/:ticket" element={withTitle("Access", <Ticket />)} />
        <SentryRoute path="/share/:ticket" element={withTitle("Access", <Ticket />)} />
        <SentryRoute path="/stories/:story_id/conclude" element={withTitle("Conclude Story", <NewProjectConclude />)} />
        <SentryRoute
          path="/projects/:project_id/stories/:story_id/conclude"
          element={withTitle("Conclude Story", <NewProjectConclude />)}
        />
        <SentryRoute
          path="/projects/:project_id/folders/:folder_id/stories/:story_id/conclude"
          element={withTitle("Conclude Story", <NewProjectConclude />)}
        />
        <SentryRoute
          path="/status/local-recording"
          element={withTitle("Local Recording Status", <LocalRecordingStatus />)}
        />
        <SentryRoute path="/tutorials" element={withTitle("Tutorials", <TutorialNodeType />)} />
        <SentryRoute path="/tutorials/:tutorial_id" element={withTitle("Tutorials", <TutorialNodeType />)} />
        <SentryRoute path="/stories/:story_id/author" element={withTitle("Author Story", <StoryAuthoring />)}>
          <SentryRoute index element={withTitle("Story Authoring", null)} />
          <SentryRoute path=":index_id/metadata" element={withTitle("Story Authoring Index", <Metadata />)} />
          <SentryRoute path=":index_id/notes" element={withTitle("Story Authoring Index: Notes Mode", <Notes />)} />
        </SentryRoute>
        <SentryRoute
          path="projects/:project_id/stories/:story_id/edit"
          element={withTitle(
            "Edit Story",
            <RequireAuth>
              <NewProjectConclude />
            </RequireAuth>
          )}
        />
        <SentryRoute
          path="projects/:project_id/folders/:folder_id/stories/:story_id/edit"
          element={withTitle(
            "Edit Story",
            <RequireAuth>
              <NewProjectConclude />
            </RequireAuth>
          )}
        />
        <SentryRoute path="/stories/:story_id/thank-you" element={withTitle("Thank You", <ThankYou />)} />
        <SentryRoute
          path="/stories/:story_id/link-expired"
          element={withTitle("Sorry, Link Expired", <LinkExpired />)}
        />
        <SentryRoute
          path="/groups/create"
          element={withTitle(
            "Create a Group",
            <RequireAuth>
              <GroupCreate />
            </RequireAuth>
          )}
        />
        <SentryRoute
          path="/groups/:group_id"
          element={withTitle(
            "Group",
            <RequireAuth>
              <GroupShow />
            </RequireAuth>
          )}
        />
        <SentryRoute
          path="/groups/:group_id/invite"
          element={withTitle(
            "Invite to Group",
            <RequireAuth>
              <GroupInvite />
            </RequireAuth>
          )}
        />
        <SentryRoute
          path="/groups/:group_id/members/:member_id/accept"
          element={withTitle("Accept Group Invite", <GroupAcceptInvite />)}
        />
        <SentryRoute
          path="/admin"
          element={withTitle(
            "Admin",
            <RequireAuth authLevel="admin">
              <Admin />
            </RequireAuth>
          )}
        />
        <SentryRoute
          path="/ticketUsed"
          element={withTitle(
            "Ticket Used",
            <RequireAuth authLevel="admin">
              <TicketUsed />
            </RequireAuth>
          )}
        />
        <SentryRoute
          path="/admin/organization/:organization_code"
          element={withTitle(
            "Manage Organization",
            <RequireAuth authLevel="manager">
              <OrganizationDetails />
            </RequireAuth>
          )}
        />
        <SentryRoute
          path="/organization/:organization_code"
          element={withTitle(
            "Manage Organization",
            <RequireAuth authLevel="org_manager">
              <OrganizationPage />
            </RequireAuth>
          )}
        />
        <SentryRoute
          path="/organization/:organization_code/projects/"
          element={withTitle(
            "Organization Projects",
            <RequireAuth>
              <OrganizationProjectsPage />
            </RequireAuth>
          )}
        />
        <SentryRoute
          path="/organization/:organization_code/projects/:project_id/folders/:folder_id"
          element={withTitle(
            "Organization Project Folder",
            <RequireAuth>
              <OrganizationProjectFolderPage />
            </RequireAuth>
          )}
        />
        <SentryRoute
          path="/organization/:organization_code/projects/:project_id"
          element={withTitle(
            "Organization Project",
            <RequireAuth>
              <OrganizationProjectPage />
            </RequireAuth>
          )}
        />
        <SentryRoute
          path="/organizations"
          element={withTitle(
            "Manage Organizations",
            <RequireAuth>
              <OrganizationsPage />
            </RequireAuth>
          )}
        />
        <SentryRoute
          path="/clips"
          element={withTitle(
            "Clips",
            <RequireAuth>
              <Clips />
            </RequireAuth>
          )}
        />
        <SentryRoute
          path="/projects"
          element={withTitle(
            "Projects",
            <RequireAuth>
              <ProjectsPage />
            </RequireAuth>
          )}
        />
        <SentryRoute
          path="/projects/:project_id"
          element={withTitle(
            "Project Stories",
            <RequireAuth>
              <ProjectPage />
            </RequireAuth>
          )}
        />
        <SentryRoute
          path="/projects/:project_id/folders/:folder_id"
          element={withTitle(
            "Folder Stories",
            <RequireAuth>
              <FolderPage />
            </RequireAuth>
          )}
        />
        <SentryRoute
          path="/projects/:project_id/uploadingMetadata"
          element={withTitle(
            "Uploading Metadata",
            <RequireAuth>
              <EditMetadataFiles />
            </RequireAuth>
          )}
        />
        {process.env.NODE_ENV === "development" && (
          <SentryRoute path="/dev/styleguide" element={withTitle("Style Guide", <StyleGuide />)} />
        )}
        <SentryRoute path="/not-found" element={withTitle("Error", Error as any)} />
        <SentryRoute path="*" element={withTitle("Page not found", <ErrorPage />)} />
      </Route>
    </Routes>
  );
}

const muiCache = createCache({
  key: "mui",
  prepend: true,
});

function AppWithNewNav() {
  const route = window.location.pathname.split("/")[1];

  return (
    <Suspense fallback={null}>
      <CacheProvider value={muiCache}>
        <MuiThemeProvider theme={theme}>
          <ScThemeProvider theme={theme}>
            <SnackbarProvider autoHideDuration={3000}>
              <DndProvider backend={HTML5Backend}>
                <ScrollingComponent>
                  <Notifier />
                  {staticSitePaths.includes(`/${route}`) && <ConditionalHeader />}
                  {AppRoutes()}
                  <ModalsContainer />
                  {staticSitePaths.includes(`/${route}`) && <ConditionalFooter />}
                </ScrollingComponent>
              </DndProvider>
            </SnackbarProvider>
          </ScThemeProvider>
        </MuiThemeProvider>
      </CacheProvider>
    </Suspense>
  );
}

// Organization of Routes
function App() {
  const routingInstrumentation = useRoutingInstrumentation();
  useEffect(() => {
    InitSentry(routingInstrumentation);
  }, [routingInstrumentation]);
  return (
    <GroupContextProvider>
      <AppWithNewNav />
    </GroupContextProvider>
  );
}

export default Sentry.withProfiler(App);
